import { useState, useEffect } from "react";
import { usePagedList } from "@/hooks/usePagedList";
import Pagination from "@/components/Pagination";
import { useLocation, useNavigate } from "react-router-dom";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown } from "lucide-react";
import { Plus, Edit, Trash2, FileText, Download, History, Eye, Upload, Trophy } from "lucide-react";
import { ManageAwardsSheet } from "@/components/estimations/ManageAwardsSheet";
import DatePicker from "@/components/DatePicker";
import BulkImportPreview from "@/components/BulkImportPreview";
import { pickableContacts, contactLabel } from "../components/contacts/ContactsTable";

/**
 * ScopeRowList — Phase 3.1 dynamic row editor for per-line scope entries.
 * Internally stores value as a newline-joined string (zero backend change).
 * Props:
 *   value       : string (newline-joined entries)
 *   onChange    : (string) => void
 *   placeholder : string shown on empty rows
 *   addLabel    : label on the "+ Add" button
 *   testIdBase  : used for data-testid of each row + add button
 */
const localTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};


function ScopeRowList({ value, onChange, placeholder, addLabel, testIdBase }) {
  const rows = (value || "").split("\n");
  const emit = (next) => onChange(next.join("\n"));
  const updateRow = (idx, text) => {
    const next = rows.slice();
    next[idx] = text;
    emit(next);
  };
  const removeRow = (idx) => {
    const next = rows.slice();
    next.splice(idx, 1);
    emit(next.length ? next : [""]);
  };
  const addRow = () => emit([...rows, ""]);
  // Enter = add new row and jump focus. Shift+Enter is free for linebreaks within a row.
  const onKeyDown = (idx) => (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const next = rows.slice();
      next.splice(idx + 1, 0, "");
      emit(next);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${testIdBase}-row-${idx + 1}"]`);
        if (el) el.focus();
      }, 20);
    }
  };
  // Auto-grow textarea height to fit wrapped content.
  const autoSize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  return (
    <div className="space-y-1">
      {rows.map((row, idx) => (
        <div key={idx} className="flex items-start gap-1">
          <textarea
            data-testid={`${testIdBase}-row-${idx}`}
            value={row}
            placeholder={placeholder}
            rows={1}
            ref={autoSize}
            onInput={(e) => autoSize(e.currentTarget)}
            onChange={(e) => updateRow(idx, e.target.value)}
            onKeyDown={onKeyDown(idx)}
            className="flex-1 min-h-[32px] py-1.5 px-3 rounded-md bg-white border border-gray-300 text-sm resize-none overflow-hidden leading-5 whitespace-pre-wrap break-words focus:outline-none focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20"
          />
          <button
            type="button"
            onClick={() => removeRow(idx)}
            data-testid={`${testIdBase}-remove-${idx}`}
            title="Remove"
            className="text-gray-400 hover:text-red-600 shrink-0 w-6 h-8 flex items-center justify-center rounded transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        data-testid={`${testIdBase}-add-btn`}
        className="text-xs text-md3-primary hover:underline flex items-center gap-1 mt-1"
      >
        <Plus className="h-3 w-3" /> {addLabel}
      </button>
    </div>
  );
}

/**
 * GroupBadge — Phase 4 inline badge rendered on grouped line item cards.
 * Click opens a popover with: rename, merge-mode select, ungroup.
 */
function GroupBadge({ group, index, colorClass, onUngroup, onRename, onMergeMode }) {
  const [open, setOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState(group.name || "");
  useEffect(() => { setNameDraft(group.name || ""); }, [group.name]);
  const label = `Group ${String.fromCharCode(65 + (index % 26))}`;
  const modeLabel = group.merge_mode === "cost" ? "Cost" : group.merge_mode === "schedule" ? "Schedule" : "Both";
  return (
    <div className="relative inline-block" data-testid={`group-badge-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`text-xs px-2 py-0.5 rounded-full border font-medium ${colorClass} hover:opacity-80`}
      >
        {label}{group.name ? `: ${group.name}` : ""} · {modeLabel} ▾
      </button>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 w-64 bg-white border border-gray-200 rounded-md shadow-lg p-3 space-y-2 text-sm">
          <div>
            <Label className="text-xs">Group Name (optional)</Label>
            <Input
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={() => onRename(nameDraft.trim() || null)}
              placeholder="e.g., Phase 1 Items"
              className="h-8 text-sm mt-1"
              data-testid={`group-name-input-${index}`}
            />
          </div>
          <div>
            <Label className="text-xs">Merge on Proposal</Label>
            <select
              value={group.merge_mode || "both"}
              onChange={(e) => onMergeMode(e.target.value)}
              className="w-full h-8 text-sm mt-1 border border-gray-300 rounded px-2 bg-white"
              data-testid={`group-mode-select-${index}`}
            >
              <option value="both">Both cost + schedule</option>
              <option value="cost">Cost only</option>
              <option value="schedule">Schedule only</option>
            </select>
          </div>
          <div className="flex justify-between pt-1 border-t">
            <button
              type="button"
              onClick={() => { setOpen(false); onUngroup(); }}
              className="text-red-600 hover:underline text-xs"
              data-testid={`group-ungroup-${index}`}
            >
              Ungroup
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-gray-600 hover:underline text-xs"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const TERMINAL_SALES_STATUS = { rejected: "Internal Rejection", cancelled: "Cancelled/Rejected By Client" };
const submissionStatusLabel = (st) => TERMINAL_SALES_STATUS[st] || (st || "").replace("_", " ");
// Award status is only meaningful once an estimation has been submitted
const showsAwardStatus = (e) =>
  !["yet_to_start", "in_progress", "completed", "reviewed"].includes(e.submission_status)
  && (e.submission_status === "submitted" || ["awarded", "partially_awarded"].includes(e.award_status));

export default function Estimations({ user }) {
  const canSetSubmitted = !!user && (
    user.access_level === "Admin"
    || user.department === "Management"
    || user.department === "PMO"
  );
  const [estimations, setEstimations] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [currentEstimation, setCurrentEstimation] = useState(null);
  const [showTeamData, setShowTeamData] = useState(false); // Toggle state for Sales Team Leads
  const [teamMemberIds, setTeamMemberIds] = useState([]); // Team member user IDs for filtering
  const [teamDataLoaded, setTeamDataLoaded] = useState(false);
  const [clientSearch, setClientSearch] = useState(""); // For client search combo box
  
  // Bulk import states
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  // RFQ conversion context — set when navigated to /estimations?from_rfq=<id>
  const location = useLocation();
  const navigate = useNavigate();
  const [convertingFromRfq, setConvertingFromRfq] = useState(null); // { id, rfq_number }
  const [estBySearch, setEstBySearch] = useState("");
  
  // RBAC-based: Redirect if user doesn't have view permission for estimations page
  useEffect(() => {
    if (user && user.permissions?.estimations?.view === false) {
      // Redirect to first accessible page
      if (user.permissions?.dashboardEstimation?.view === true) {
        window.location.href = "/dashboard-estimation";
      } else if (user.permissions?.dashboard?.view === true) {
        window.location.href = "/dashboard";
      } else {
        window.location.href = "/";
      }
    }
  }, [user]);

  // Read ?from_rfq= query param → prefill new-estimation dialog from an existing RFQ
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rfqId = params.get("from_rfq");
    if (!rfqId) return;
    (async () => {
      try {
        const res = await api.get(`/rfqs/${rfqId}`);
        const rfq = res.data;
        if (rfq.status !== "initiated") {
          toast.error(`RFQ ${rfq.rfq_number} is already ${rfq.status} — cannot convert`);
          navigate("/estimations", { replace: true });
          return;
        }
        setConvertingFromRfq({ id: rfq.id, rfq_number: rfq.rfq_number });
        setFormData((prev) => {
          // Carry the RFQ notes into the Remarks / Special Instructions box.
          // Prepend the source RFQ number so the estimator knows where the context came from.
          // Guard against duplicate prefill (React StrictMode double-invokes effects in dev).
          const rfqMarker = `[From RFQ ${rfq.rfq_number}]`;
          const alreadyCarried = (prev.remarks || "").includes(rfqMarker);
          const nextRemarks = rfq.notes && !alreadyCarried
            ? `${rfqMarker}\n${rfq.notes}${prev.remarks ? `\n\n${prev.remarks}` : ""}`
            : prev.remarks;
          return {
            ...prev,
            client_id: rfq.client_id || "",
            client_contact_id: rfq.client_contact_id || prev.client_contact_id || "",
            project_name: rfq.project_name || "",
            due_date: rfq.due_date || "",
            estimation_received_date: rfq.received_date || prev.estimation_received_date || "",
            high_priority: !!rfq.high_priority,
            remarks: nextRemarks,
          };
        });
        // Populate the visible client search input so the user sees the client name.
        // Prefer live client lookup (has client_number) — fall back to denormalized RFQ.client_name.
        if (rfq.client_id) {
          try {
            const cRes = await api.get(`/clients/${rfq.client_id}`);
            setClientSearch(getClientDisplay(cRes.data));
            setFormData((prev) => ({
              ...prev,
              currency: cRes.data?.currency || prev.currency,
              software: cRes.data?.software?.[0] || prev.software,
            }));
          } catch {
            setClientSearch(rfq.client_name || "");
          }
          // Autofill salesperson from the client's current assignment (editable); fall back to RFQ owner
          let salesUserId = rfq.sales_owner || "";
          try {
            const sRes = await api.get(`/clients/${rfq.client_id}/current-sales-user`);
            if (sRes.data?.sales_user_id) salesUserId = sRes.data.sales_user_id;
          } catch {
            // no active assignment for this client
          }
          if (salesUserId) setFormData((prev) => ({ ...prev, sales: salesUserId }));
        }
        setEditMode(false);
        setCurrentEstimation(null);
        setDialogOpen(true);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load RFQ for conversion");
      }
    })();
  }, [location.search]);

  // Close client dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('client-dropdown');
      const input = event.target.closest('input[placeholder*="Type to search client"]');
      if (dropdown && !dropdown.contains(event.target) && !input) {
        dropdown.style.display = 'none';
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // RBAC-based permission helpers using user.permissions.estimations
  const canCreate = () => user?.permissions?.estimations?.create === true;
  const canEdit = () => user?.permissions?.estimations?.edit === true;
  const canDelete = () => user?.permissions?.estimations?.delete === true;
  const canExport = () => user?.permissions?.estimations?.export === true;
  const canApprove = () => user?.permissions?.estimations?.approve === true;

  // Helper function to check if user can view cost information
  // This is based on department for business reasons (cost visibility is sensitive)
  const canViewCosts = () => {
    return user?.department === "Management" || 
           user?.department === "Sales" || 
           user?.department === "PMO" ||
           user?.department === "Accounts and Finance";
  };

  // Helper function to check if user is Estimation team member (restricted view)
  const isEstimationTeamMember = () => {
    return user?.department === "Estimation" && user?.access_level === "Team Member";
  };

  // Helper function to check if user is Sales Team Member (restricted view)
  const isSalesTeamMember = () => {
    return user?.department === "Sales" && user?.access_level === "Team Member";
  };

  // Helper function to check if user is Sales Team Lead
  const isSalesTeamLead = () => {
    return user?.department === "Sales" && user?.access_level === "Team Lead";
  };

  // Filter clients based on search
  const getFilteredClients = () => {
    if (!clientSearch) return clients;
    const search = clientSearch.toLowerCase();
    return clients.filter(c => 
      (c.client_number || "").toLowerCase().includes(search) ||
      (c.client_name || "").toLowerCase().includes(search) ||
      (c.name || "").toLowerCase().includes(search)
    );
  };

  // Fetch team member IDs for Sales Team Lead using backend endpoint
  const fetchTeamMembers = async () => {
    if (!user) {
      setTeamMemberIds([]);
      setTeamDataLoaded(true);
      return;
    }
    
    try {
      console.log("Estimations - Fetching team members for user:", user.username);
      const response = await api.get("/teams/my-team-members");
      const ids = response.data.user_ids || [];
      setTeamMemberIds(ids);
      setTeamDataLoaded(true);
      console.log("Estimations - Team member IDs fetched:", ids);
      
      // If we got more than just current user's ID, user is a team lead
      if (ids.length > 1) {
        console.log(`✓ User is a Team Lead with ${ids.length} team members`);
      } else {
        console.log(`User is not a team lead (only 1 ID returned)`);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      // Fallback to just current user
      setTeamMemberIds([user.id]);
      setTeamDataLoaded(true);
    }
  };

  // Helper functions for line items
  const calculateLineItemSubtotal = (item) => {
    return (parseFloat(item.detailing_cost) || 0) + (parseFloat(item.engineering_cost) || 0);
  };

  const calculateTotals = (lineItems) => {
    let totalDetailingCost = 0;
    let totalEngineeringCost = 0;
    let totalEstimatedHours = 0;
    let totalDays = 0;

    lineItems.forEach(item => {
      totalDetailingCost += parseFloat(item.detailing_cost) || 0;
      totalEngineeringCost += parseFloat(item.engineering_cost) || 0;
      totalEstimatedHours += parseFloat(item.estimated_detailing_hours) || 0;
      const weeks = parseInt(item.duration_weeks) || 0;
      const days = parseInt(item.duration_days) || 0;
      totalDays += (weeks * 5) + days;
    });

    const totalWeeks = Math.floor(totalDays / 5);
    const remainingDays = totalDays % 5;

    return {
      totalDetailingCost,
      totalEngineeringCost,
      totalProjectValue: totalDetailingCost + totalEngineeringCost,
      totalEstimatedHours,
      totalWeeks,
      totalDays: remainingDays
    };
  };

  const handleAddLineItem = () => {
    setFormData({
      ...formData,
      line_items: [
        ...formData.line_items,
        {
          id: crypto.randomUUID(),
          description: "",
          detailing_cost: 0,
          engineering_cost: 0,
          estimated_detailing_hours: 0,
          subtotal: 0,
          duration_weeks: 0,
          duration_days: 0,
          scope_inclusions: "",
          scope_exclusions: ""
        }
      ]
    });
  };

  const handleRemoveLineItem = (id) => {
    if (formData.line_items.length === 1) {
      toast.error("At least one line item is required");
      return;
    }
    setFormData({
      ...formData,
      line_items: formData.line_items.filter(item => item.id !== id)
    });
  };

  const handleLineItemChange = (id, field, value) => {
    const updatedItems = formData.line_items.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        updated.subtotal = calculateLineItemSubtotal(updated);
        return updated;
      }
      return item;
    });
    setFormData({ ...formData, line_items: updatedItems });
  };
  
  // Filter states
  const [filters, setFilters] = useState({
    client_id: "",
    estimation_number: "",
    project_name: "",
    submission_status: "",
    award_status: "",
    due_date: ""
  });

  // Confirmation dialog before creating a new version
  const [versionConfirmDialog, setVersionConfirmDialog] = useState({ open: false, changes: [] });

  // Phase 2 — Manage Awards slide-out
  const [awardsSheet, setAwardsSheet] = useState({ open: false, estimationId: null });

  // Phase 3 — Track which line items have their per-item Scope section expanded
  const [scopeOpenItems, setScopeOpenItems] = useState(new Set());
  const toggleScopeOpen = (itemId) => {
    setScopeOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  // Phase 4 — Proposal item-grouping multi-select state
  const [selectedForGrouping, setSelectedForGrouping] = useState(new Set());
  const toggleSelectForGrouping = (itemId) => {
    setSelectedForGrouping(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };
  const clearSelection = () => setSelectedForGrouping(new Set());

  // Phase 4.1 — Per-line-item card collapse (collapsed by default on load, new items start expanded)
  const [collapsedItems, setCollapsedItems] = useState(new Set());
  const toggleItemCollapsed = (itemId) => {
    setCollapsedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };
  const collapseAll = () => {
    setCollapsedItems(new Set((formData.line_items || []).map(li => li.id)));
  };
  const expandAll = () => setCollapsedItems(new Set());

  // Helpers to work with formData.proposal_groups ([{id, name, line_item_ids, merge_mode}])
  const getGroupForItem = (lineItemId) => {
    const groups = formData.proposal_groups || [];
    return groups.find(g => (g.line_item_ids || []).includes(lineItemId));
  };
  const groupIndexForItem = (lineItemId) => {
    const groups = formData.proposal_groups || [];
    return groups.findIndex(g => (g.line_item_ids || []).includes(lineItemId));
  };
  const groupColors = [
    "bg-rose-100 border-rose-400 text-rose-800",
    "bg-sky-100 border-sky-400 text-sky-800",
    "bg-emerald-100 border-emerald-400 text-emerald-800",
    "bg-violet-100 border-violet-400 text-violet-800",
    "bg-amber-100 border-amber-400 text-amber-800",
  ];
  const groupStripeColors = [
    "border-l-rose-500",
    "border-l-sky-500",
    "border-l-emerald-500",
    "border-l-violet-500",
    "border-l-amber-500",
  ];

  const groupSelected = () => {
    const ids = Array.from(selectedForGrouping);
    if (ids.length < 2) {
      toast.error("Select at least 2 line items to group.");
      return;
    }
    // Enforce Q2b — all selected items must share the same award_status
    const statuses = new Set(ids.map(id => {
      const li = formData.line_items.find(l => l.id === id);
      return (li?.award_status || "pending").toLowerCase();
    }));
    if (statuses.size > 1) {
      toast.error("Can only group line items with the same award status.");
      return;
    }
    // Remove these ids from any existing groups first (one-group-per-item rule, Q1)
    const existing = (formData.proposal_groups || []).map(g => ({
      ...g,
      line_item_ids: (g.line_item_ids || []).filter(x => !ids.includes(x))
    })).filter(g => (g.line_item_ids || []).length >= 2);
    existing.push({
      id: crypto.randomUUID(),
      name: null,
      line_item_ids: ids,
      merge_mode: "both",
    });
    setFormData({ ...formData, proposal_groups: existing });
    clearSelection();
    toast.success(`Grouped ${ids.length} line items.`);
  };

  const ungroup = (groupId) => {
    const next = (formData.proposal_groups || []).filter(g => g.id !== groupId);
    setFormData({ ...formData, proposal_groups: next });
  };

  const updateGroup = (groupId, patch) => {
    const next = (formData.proposal_groups || []).map(g => g.id === groupId ? { ...g, ...patch } : g);
    setFormData({ ...formData, proposal_groups: next });
  };
  
  const [formData, setFormData] = useState({
    estimation_number: "",
    client_id: "",
    project_name: "",
    uses_item_wise_scope: true,  // New estimations default to item-wise
    proposal_groups: [],
    standard_exclusions: "Connection design\nPiping, Pipe Sleeves & Pipe Fitting\nElectrical & Support Items\nPEMB Structure\nLight gauge metals\nGlass Material & Ornamental fittings\nWood Member & Wood Connection\nAluminum & Stainless-steel Materials\nFence, Metal Panel & Precast Panel\nJoist, Joist Bridging, Metal Deck & Stud\nOther items not mentioned in the above inclusions.",
    line_items: [
      {
        id: crypto.randomUUID(),
        description: "Structural and Miscellaneous",
        detailing_cost: 0,
        engineering_cost: 0,
        estimated_detailing_hours: 0,
        subtotal: 0,
        duration_weeks: 0,
        duration_days: 0,
        scope_inclusions: "",
        scope_exclusions: ""
      }
    ],
    show_itemwise_schedule: false,
    detailing_cost: "",
    engineering_cost: "",
    currency: "USD",
    software: "",
    schedule_weeks: "0",
    schedule_days: "0",
    estimated_by: [],
    priced_by: "",
    sales: "",
    submission_status: "yet_to_start",
    sales_tracking_status: "",
    award_status: "pending",
    estimation_received_date: "",
    due_date: "",
    submitted_date: "",
    awarded_date: "",
    high_priority: false,
    remarks: "",
    status_reason: "",
    status_date: "",
    scope_inclusions: "",
    scope_exclusions: "Connection design\nPiping, Pipe Sleeves & Pipe Fitting\nElectrical & Support Items\nPEMB Structure\nLight gauge metals\nGlass Material & Ornamental fittings\nWood Member & Wood Connection\nAluminum & Stainless-steel Materials\nFence, Metal Panel & Precast Panel\nJoist, Joist Bridging, Metal Deck & Stud\nOther items not mentioned in the above inclusions.",
    description: "",
    delivery_process: "AB drawings and setting plans\nErection drawings\nShop drawings\nDSTV, DXF & KSS files\nBolt lists",
    client_contact_id: ""
  });

  useEffect(() => {
    fetchEstimations();
    fetchClients();
    fetchUsers();
    fetchTeamMembers(); // Always fetch - backend decides if user is team lead
  }, []);
  
  // Debug: Log when toggle changes
  useEffect(() => {
    if (isSalesTeamLead()) {
      console.log("Estimations - Toggle changed - showTeamData:", showTeamData);
      console.log("Estimations - Team member IDs:", teamMemberIds);
    }
  }, [showTeamData, teamMemberIds]);

  // Fetch contacts when client changes
  useEffect(() => {
    if (formData.client_id) {
      fetchContactsForClient(formData.client_id);
    } else {
      setContacts([]);
    }
  }, [formData.client_id]);

  const fetchEstimations = async () => {
    try {
      const response = await api.get("/estimations");
      // Sort descending by estimation_number (newest first) — numeric-aware so
      // "25ES1234" > "25ES099" and "EST-200" > "EST-99".
      const sorted = [...(response.data || [])].sort((a, b) =>
        (b.estimation_number || "").localeCompare(a.estimation_number || "", undefined, { numeric: true, sensitivity: "base" })
      );
      setEstimations(sorted);
    } catch (error) {
      toast.error("Failed to fetch estimations");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients");
    }
  };

  const fetchContactsForClient = async (clientId) => {
    try {
      const response = await api.get(`/contacts?client_id=${clientId}`);
      setContacts(response.data);
    } catch (error) {
      console.error("Failed to fetch contacts");
      setContacts([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  const handleViewHistory = async (estimation) => {
    try {
      const response = await api.get(`/estimations/${estimation.id}/history`);
      setVersionHistory(response.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch version history");
    }
  };


  const handlePreviewProposal = async (estimationId) => {
    if (!estimationId) {
      toast.error("Invalid estimation ID");
      return;
    }
    
    // Open the server URL directly in a new tab (not a blob URL). The browser
    // then receives Content-Disposition from the server and uses that filename
    // for the preview tab and the Save As dialog. Auth travels via query token.
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please sign in again to preview the proposal.");
        return;
      }
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = `${base}/api/estimations/${estimationId}/proposal-pdf?preview=true&token=${encodeURIComponent(token)}`;
      const newTab = window.open(url, "_blank");
      if (!newTab) {
        toast.error("Please allow popups to preview proposals");
        return;
      }
      toast.success("Proposal opened in new tab!");
    } catch (error) {
      console.error("Proposal preview error:", error);
      toast.error("Failed to preview proposal.");
    }
  };

  const handleDownloadProposal = async (estimationId) => {
    try {
      const response = await api.get(`/estimations/${estimationId}/proposal-pdf?preview=false`, {
        responseType: 'blob'
      });
      
      // Check if response is actually a blob (PDF) or error JSON
      if (response.data.type === 'application/json') {
        // If error came as JSON in blob, parse it
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || "Failed to generate proposal");
      }
      
      // Extract filename from content-disposition header or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'proposal.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Proposal downloaded successfully!");
    } catch (error) {
      console.error("Proposal download error:", error);
      
      // Better error message handling
      let errorMessage = "Failed to download proposal.";
      
      if (error.response) {
        // Try to parse error from blob response
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.detail || errorMessage;
          } catch (e) {
            errorMessage = error.response.statusText || errorMessage;
          }
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };


  // Compute the diff between current loaded estimation version and pending edits.
  // Only the 3 fields the user asked to surface on the confirm-new-version dialog.
  const computeVersionChanges = () => {
    if (!currentEstimation) return [];
    const changes = [];

    // 1) Total project value
    const totals = calculateTotals(formData.line_items);
    const newTotalValue = totals.totalProjectValue || 0;
    const oldTotalValue = currentEstimation.total_project_value || 0;
    if (Math.abs(newTotalValue - oldTotalValue) > 0.001) {
      changes.push({
        field: "Total Project Value",
        old: formatCurrency(oldTotalValue, currentEstimation.currency || formData.currency),
        new: formatCurrency(newTotalValue, formData.currency || currentEstimation.currency),
      });
    }

    // 2) Total duration (weeks + days)
    const newDays = ((totals.totalWeeks || 0) * 5) + (totals.totalDays || 0);
    const oldDays = ((currentEstimation.schedule_weeks || 0) * 5) + (currentEstimation.schedule_days || 0);
    if (newDays !== oldDays) {
      changes.push({
        field: "Total Duration",
        old: `${currentEstimation.schedule_weeks || 0}W ${currentEstimation.schedule_days || 0}D`,
        new: `${totals.totalWeeks || 0}W ${totals.totalDays || 0}D`,
      });
    }

    // 3) Scope of Work — Inclusions
    const oldScope = (currentEstimation.scope_inclusions || "").trim();
    const newScope = (formData.scope_inclusions || "").trim();
    if (oldScope !== newScope) {
      changes.push({
        field: "Scope of Work – Inclusions",
        old: oldScope || "(empty)",
        new: newScope || "(empty)",
        isText: true,
      });
    }

    return changes;
  };


  const handleSubmit = async (e, createNewVersion = false) => {
    e.preventDefault();
    
    // Validate mandatory fields
    // For Estimation Team Members: client_id field is hidden but must be preserved from original record
    if (!formData.client_id && !isEstimationTeamMember()) {
      toast.error("Please select a client");
      return;
    }
    if (!formData.project_name.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    
    // Require a reason when status is Hold/Cancelled/Rejected
    if (["hold", "cancelled", "rejected"].includes((formData.submission_status || "").toLowerCase())) {
      if (!formData.status_date) {
        toast.error(`Please provide the ${formData.submission_status} date.`);
        return;
      }
      if (!(formData.status_reason || "").trim()) {
        toast.error(`Please provide a reason for marking this as ${formData.submission_status}.`);
        return;
      }
    }

    // Validate: Estimated Detailing Hours required when a line has Detailing Cost > 0
    const invalidHoursItem = formData.line_items.find(
      (it) => (parseFloat(it.detailing_cost) || 0) > 0 && (parseFloat(it.estimated_detailing_hours) || 0) <= 0
    );
    if (invalidHoursItem) {
      toast.error("Estimated Detailing Hours is required for every line item with a Detailing Cost.");
      return;
    }

    // New estimations: line-item Duration (Days) must be 0–4 (5-day work week). Older imported data stays editable.
    if (!editMode) {
      const badDays = formData.line_items.find((it) => (parseInt(it.duration_days) || 0) > 4);
      if (badDays) {
        toast.error(`Duration (Days) must be 0–4 for "${badDays.description || "line item"}". Use weeks for 5 days or more.`);
        return;
      }
    }
    
    // Calculate totals from line items
    const totals = calculateTotals(formData.line_items);
    
    try {
      // Log before sending to backend
      console.log("FormData sales_tracking_status before payload:", formData.sales_tracking_status);
      
      const payload = {
        ...formData,
        // For Estimation Team Members: explicitly preserve hidden fields from currentEstimation
        client_id: formData.client_id || (isEstimationTeamMember() && editMode ? currentEstimation.client_id : formData.client_id),
        sales: formData.sales || (isEstimationTeamMember() && editMode ? currentEstimation.sales : null) || null,
        client_contact_id: formData.client_contact_id || null,  // Explicitly include client contact
        estimated_hours: totals.totalEstimatedHours,  // Derived from sum of line items
        line_items: formData.line_items,
        detailing_cost: totals.totalDetailingCost,
        engineering_cost: totals.totalEngineeringCost,
        schedule_weeks: totals.totalWeeks,
        schedule_days: totals.totalDays,
        estimation_received_date: formData.estimation_received_date || null,
        due_date: formData.due_date || null,
        high_priority: !!formData.high_priority,
        submitted_date: formData.submitted_date || null,
        awarded_date: formData.awarded_date || null,
        remarks: formData.remarks || null,
        status_reason: (["hold", "cancelled", "rejected"].includes((formData.submission_status || "").toLowerCase()) ? (formData.status_reason || null) : null),
        status_date: (["hold", "cancelled", "rejected"].includes((formData.submission_status || "").toLowerCase()) ? (formData.status_date || null) : null),
        scope_inclusions: formData.scope_inclusions || null,
        scope_exclusions: formData.scope_exclusions || null,
        standard_exclusions: formData.standard_exclusions || null,
        uses_item_wise_scope: !!formData.uses_item_wise_scope,
        proposal_groups: Array.isArray(formData.proposal_groups) ? formData.proposal_groups : [],
        description: formData.description || null,
        estimated_by: Array.isArray(formData.estimated_by)
          ? (formData.estimated_by.length === 0 ? null : formData.estimated_by)
          : (formData.estimated_by || null),
        priced_by: formData.priced_by || null
        // sales_tracking_status comes from ...formData spread - don't override
      };
      
      // Debug logging
      console.log("DEBUG: client_contact_id in payload:", payload.client_contact_id);
      
      console.log("Payload sales_tracking_status being sent:", payload.sales_tracking_status);

      let response;
      if (editMode) {
        if (createNewVersion) {
          // PATCH - Create new version
          response = await api.patch(`/estimations/${currentEstimation.id}`, payload);
          toast.success("New version created successfully!");
        } else {
          // PUT - Update same version
          response = await api.put(`/estimations/${currentEstimation.id}`, payload);
          toast.success("Estimation updated successfully!");
        }
        
        // Update currentEstimation and formData with the response to keep form in sync
        if (response?.data) {
          const updatedEstimation = response.data;
          
          // Ensure line_items exist and have proper structure
          const lineItems = updatedEstimation.line_items && updatedEstimation.line_items.length > 0
            ? updatedEstimation.line_items.map(item => ({
                ...item,
                id: item.id || crypto.randomUUID(),
                estimated_detailing_hours: item.estimated_detailing_hours || 0,
                scope_inclusions: item.scope_inclusions || "",
                scope_exclusions: item.scope_exclusions || ""
              }))
            : [{
                id: crypto.randomUUID(),
                description: "Structural and Miscellaneous",
                detailing_cost: updatedEstimation.detailing_cost || 0,
                engineering_cost: updatedEstimation.engineering_cost || 0,
                estimated_detailing_hours: updatedEstimation.estimated_hours || 0,
                subtotal: (updatedEstimation.detailing_cost || 0) + (updatedEstimation.engineering_cost || 0),
                duration_weeks: updatedEstimation.schedule_weeks || 0,
                duration_days: updatedEstimation.schedule_days || 0,
                scope_inclusions: "",
                scope_exclusions: ""
              }];
          
          setCurrentEstimation(updatedEstimation);
          // Ensure sales_tracking_status value matches what backend returned
          console.log("Backend returned sales_tracking_status:", updatedEstimation.sales_tracking_status);
          
          setFormData({
            estimation_number: updatedEstimation.estimation_number,
            client_id: updatedEstimation.client_id,
            project_name: updatedEstimation.project_name,
            uses_item_wise_scope: !!updatedEstimation.uses_item_wise_scope,
            proposal_groups: Array.isArray(updatedEstimation.proposal_groups) ? updatedEstimation.proposal_groups : [],
            standard_exclusions: updatedEstimation.standard_exclusions || "",
            line_items: lineItems,
            show_itemwise_schedule: updatedEstimation.show_itemwise_schedule || false,
            detailing_cost: updatedEstimation.detailing_cost?.toString() || "0",
            engineering_cost: updatedEstimation.engineering_cost?.toString() || "0",
            currency: updatedEstimation.currency || "USD",
            software: updatedEstimation.software || "",
            schedule_weeks: updatedEstimation.schedule_weeks?.toString() || "0",
            schedule_days: updatedEstimation.schedule_days?.toString() || "0",
            estimated_by: Array.isArray(updatedEstimation.estimated_by)
              ? updatedEstimation.estimated_by
              : (updatedEstimation.estimated_by ? [updatedEstimation.estimated_by] : []),
            priced_by: updatedEstimation.priced_by,
            sales: updatedEstimation.sales || "",
            submission_status: updatedEstimation.submission_status,
            // Keep the actual value from backend - don't convert to empty string if it exists
            sales_tracking_status: updatedEstimation.sales_tracking_status || "",
            award_status: updatedEstimation.award_status,
            estimation_received_date: updatedEstimation.estimation_received_date || "",
            due_date: updatedEstimation.due_date || "",
            submitted_date: updatedEstimation.submitted_date || "",
            awarded_date: updatedEstimation.awarded_date || "",
            remarks: updatedEstimation.remarks || "",
            status_reason: updatedEstimation.status_reason || "",
            status_date: updatedEstimation.status_date || "",
            scope_inclusions: updatedEstimation.scope_inclusions || "",
            scope_exclusions: updatedEstimation.scope_exclusions || "Connection design\nPiping, Pipe Sleeves & Pipe Fitting\nElectrical & Support Items\nPEMB Structure\nLight gauge metals\nGlass Material & Ornamental fittings\nWood Member & Wood Connection\nAluminum & Stainless-steel Materials\nFence, Metal Panel & Precast Panel\nJoist, Joist Bridging, Metal Deck & Stud\nOther items not mentioned in the above inclusions.",
            description: updatedEstimation.description || "",
            delivery_process: updatedEstimation.delivery_process || "AB drawings and setting plans\nErection drawings\nShop drawings\nDSTV, DXF & KSS files\nBolt lists",
            client_contact_id: updatedEstimation.client_contact_id || ""
          });
          
          console.log("FormData set to sales_tracking_status:", updatedEstimation.sales_tracking_status || "");
        }
      } else {
        response = await api.post("/estimations", payload);
        // If we came from an RFQ, mark the RFQ as converted and link it
        if (convertingFromRfq && response?.data?.id) {
          try {
            await api.post(`/rfqs/${convertingFromRfq.id}/convert`, { estimation_id: response.data.id });
            toast.success(`Estimation created from RFQ ${convertingFromRfq.rfq_number}`);
          } catch (rfqErr) {
            console.error("Failed to mark RFQ converted", rfqErr);
            toast.success("Estimation created (RFQ link update failed — please check RFQ page)");
          }
          setConvertingFromRfq(null);
          navigate("/estimations", { replace: true });
        } else {
          toast.success("Estimation created successfully!");
        }
        setDialogOpen(false);
        resetForm();
        // Refresh the list after creating
        await fetchEstimations();
      }
      
      // For edit mode, DON'T refresh list yet - keep form stable
      // List will refresh when user closes dialog
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} estimation`);
    }
  };

  const handleEdit = async (rowEstimation) => {
    // Fetch fresh full record — list endpoint slims heavy fields (line_items, scope_inclusions, etc.)
    let estimation = rowEstimation;
    try {
      const res = await api.get(`/estimations/${rowEstimation.id}`);
      estimation = res.data;
    } catch (err) {
      console.error("Failed to fetch full estimation, falling back to list row", err);
    }
    setCurrentEstimation(estimation);
    // Ensure line_items exist and have proper structure
    const lineItems = estimation.line_items && estimation.line_items.length > 0
      ? estimation.line_items.map(item => ({
          ...item,
          id: item.id || crypto.randomUUID(),
          estimated_detailing_hours: item.estimated_detailing_hours || 0,
          scope_inclusions: item.scope_inclusions || "",
          scope_exclusions: item.scope_exclusions || ""
        }))
      : [{
          id: crypto.randomUUID(),
          description: "Structural and Miscellaneous",
          detailing_cost: estimation.detailing_cost || 0,
          engineering_cost: estimation.engineering_cost || 0,
          estimated_detailing_hours: estimation.estimated_hours || 0,
          subtotal: (estimation.detailing_cost || 0) + (estimation.engineering_cost || 0),
          duration_weeks: estimation.schedule_weeks || 0,
          duration_days: estimation.schedule_days || 0,
          scope_inclusions: "",
          scope_exclusions: ""
        }];
    
    // Find the client to populate the search box
    const selectedClient = clients.find(c => c.id === estimation.client_id);
    if (selectedClient) {
      setClientSearch(getClientDisplay(selectedClient));
    }
    
    setFormData({
      estimation_number: estimation.estimation_number,
      client_id: estimation.client_id,
      project_name: estimation.project_name,
      uses_item_wise_scope: !!estimation.uses_item_wise_scope,
      proposal_groups: Array.isArray(estimation.proposal_groups) ? estimation.proposal_groups : [],
      standard_exclusions: estimation.standard_exclusions || "",
      line_items: lineItems,
      show_itemwise_schedule: estimation.show_itemwise_schedule || false,
      detailing_cost: estimation.detailing_cost?.toString() || "0",
      engineering_cost: estimation.engineering_cost?.toString() || "0",
      currency: estimation.currency || "USD",
      software: estimation.software || "",
      schedule_weeks: estimation.schedule_weeks?.toString() || "0",
      schedule_days: estimation.schedule_days?.toString() || "0",
      estimated_by: Array.isArray(estimation.estimated_by)
        ? estimation.estimated_by
        : (estimation.estimated_by ? [estimation.estimated_by] : []),
      priced_by: estimation.priced_by,
      sales: estimation.sales || "",
      submission_status: estimation.submission_status,
      sales_tracking_status: estimation.sales_tracking_status || "",
      award_status: estimation.award_status,
      estimation_received_date: estimation.estimation_received_date || "",
      due_date: estimation.due_date || "",
      high_priority: !!estimation.high_priority,
      submitted_date: estimation.submitted_date || "",
      awarded_date: estimation.awarded_date || "",
      remarks: estimation.remarks || "",
      status_reason: estimation.status_reason || "",
      status_date: estimation.status_date || (["hold", "cancelled", "rejected"].includes(estimation.submission_status) ? localTodayISO() : ""),
      scope_inclusions: estimation.scope_inclusions || "",
      scope_exclusions: estimation.scope_exclusions || "Connection design\nPiping, Pipe Sleeves & Pipe Fitting\nElectrical & Support Items\nPEMB Structure\nLight gauge metals\nGlass Material & Ornamental fittings\nWood Member & Wood Connection\nAluminum & Stainless-steel Materials\nFence, Metal Panel & Precast Panel\nJoist, Joist Bridging, Metal Deck & Stud\nOther items not mentioned in the above inclusions.",
      description: estimation.description || "",
      delivery_process: estimation.delivery_process || "AB drawings and setting plans\nErection drawings\nShop drawings\nDSTV, DXF & KSS files\nBolt lists",
      client_contact_id: estimation.client_contact_id || ""
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (estimationId) => {
    if (!window.confirm("Are you sure you want to delete this estimation?")) return;
    try {
      await api.delete(`/estimations/${estimationId}`);
      toast.success("Estimation deleted successfully");
      fetchEstimations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete estimation");
    }
  };

  const resetForm = () => {
    setFormData({
      estimation_number: "",
      client_id: "",
      project_name: "",
      uses_item_wise_scope: true,
      proposal_groups: [],
      standard_exclusions: "Connection design\nPiping, Pipe Sleeves & Pipe Fitting\nElectrical & Support Items\nPEMB Structure\nLight gauge metals\nGlass Material & Ornamental fittings\nWood Member & Wood Connection\nAluminum & Stainless-steel Materials\nFence, Metal Panel & Precast Panel\nJoist, Joist Bridging, Metal Deck & Stud\nOther items not mentioned in the above inclusions.",
      line_items: [
        {
          id: crypto.randomUUID(),
          description: "Structural and Miscellaneous",
          detailing_cost: 0,
          engineering_cost: 0,
          estimated_detailing_hours: 0,
          subtotal: 0,
          duration_weeks: 0,
          duration_days: 0,
          scope_inclusions: "",
          scope_exclusions: ""
        }
      ],
      show_itemwise_schedule: false,
      detailing_cost: "",
      engineering_cost: "",
      currency: "USD",
      software: "",
      schedule_weeks: "0",
      schedule_days: "0",
      estimated_by: [],
      priced_by: "",
      sales: "",
      submission_status: "yet_to_start",
      sales_tracking_status: "",
      award_status: "pending",
      estimation_received_date: "",
      due_date: "",
      high_priority: false,
      submitted_date: "",
      awarded_date: "",
      remarks: "",
      status_reason: "",
      status_date: "",
      scope_inclusions: "",
      scope_exclusions: "Connection design\nPiping, Pipe Sleeves & Pipe Fitting\nElectrical & Support Items\nPEMB Structure\nLight gauge metals\nGlass Material & Ornamental fittings\nWood Member & Wood Connection\nAluminum & Stainless-steel Materials\nFence, Metal Panel & Precast Panel\nJoist, Joist Bridging, Metal Deck & Stud\nOther items not mentioned in the above inclusions.",
      description: "",
      delivery_process: "AB drawings and setting plans\nErection drawings\nShop drawings\nDSTV, DXF & KSS files\nBolt lists"
    });
    setClientSearch(""); // Reset client search
    setEditMode(false);
    setCurrentEstimation(null);
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      // Dialog is closing
      if (editMode) {
        // Refresh list after editing (only when closing)
        fetchEstimations();
      }
      // If user cancelled a from-RFQ conversion, clear query param and context
      if (convertingFromRfq) {
        setConvertingFromRfq(null);
        navigate("/estimations", { replace: true });
      }
      resetForm();
    }
  };

  const handleDownloadExcel = async () => {
    try {
      console.log("Starting Excel download...");
      const response = await api.get("/export/estimations", {
        responseType: 'blob'
      });
      
      console.log("Response received:", response);
      console.log("Response data type:", response.data?.constructor?.name);
      
      if (!(response.data instanceof Blob)) {
        console.error("Invalid response type:", response.data);
        throw new Error("Invalid response type - expected Blob");
      }
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `estimations_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Estimations downloaded successfully!");
    } catch (error) {
      console.error("Excel download error:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      const errorMessage = error.response?.data?.detail || error.message || "Failed to download estimations";
      toast.error(errorMessage);
    }
  };

  const totalProjectValue = (parseFloat(formData.detailing_cost) || 0) + (parseFloat(formData.engineering_cost) || 0);

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return "Unknown";
    const number = client.client_number || "N/A";
    const name = client.client_name || client.company || "Unknown";
    return `${number} - ${name}`;
  };

  const getClientDisplay = (client) => {
    const number = client.client_number || "N/A";
    const name = client.client_name || client.company || "Unknown";
    return `${number} - ${name}`;
  };

  const getUserName = (userId) => {
    const userObj = users.find(u => u.id === userId);
    return userObj ? `${userObj.first_name} ${userObj.last_name || ''}`.trim() : "Unknown";
  };

  const getSubmissionStatusColor = (status) => {
    switch (status) {
      case "yet_to_start":
        return "bg-gray-500";
      case "in_progress":
        return "bg-blue-500";
      case "completed":
        return "bg-indigo-500";
      case "reviewed":
        return "bg-teal-500";
      case "hold":
        return "bg-yellow-500";
      case "cancelled":
        return "bg-red-500";
      case "rejected":
        return "bg-red-600";
      case "submitted":
        return "bg-green-600";
      default:
        return "bg-gray-500";
    }
  };

  const getAwardStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-500";
      case "partially_awarded":
        return "bg-orange-500";
      case "awarded":
        return "bg-green-500";
      case "lost":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // Handle apply bulk import
  const handleApplyImport = async (selectedNew, selectedUpdates) => {
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(uploadedFile);
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        const payload = {
          file_content: base64,
          selected_new: selectedNew,
          selected_updates: selectedUpdates
        };
        
        const response = await api.post("/import/estimations/apply", payload);
        
        if (response.data.success) {
          toast.success(`Successfully created ${response.data.created} and updated ${response.data.updated} estimations!`);
          fetchEstimations();
          setImportPreviewOpen(false);
        }
        
        if (response.data.errors && response.data.errors.length > 0) {
          console.log("Import errors:", response.data.errors);
          toast.warning(`Import completed with ${response.data.errors.length} errors. Check console for details.`);
        }
      };
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply import");
    }
  };

  const formatCurrency = (amount, currency) => {
    return `$${amount?.toLocaleString()} ${currency}`;
  };

  const clearFilters = () => {
    setFilters({
      client_id: "",
      estimation_number: "",
      project_name: "",
      submission_status: "",
      award_status: "",
      due_date: ""
    });
  };

  // Filter estimations based on filter criteria
  const filteredEstimations = estimations.filter(estimation => {
    // Client filter - search by client number OR client name
    if (filters.client_id) {
      const client = clients.find(c => c.id === estimation.client_id);
      const clientNumber = client ? (client.client_number || "") : "";
      const clientName = client ? (client.client_name || client.company || "") : "";
      const searchTerm = filters.client_id.toLowerCase();
      
      // Check if search term is in EITHER client number OR client name
      if (!clientNumber.toLowerCase().includes(searchTerm) && 
          !clientName.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    if (filters.estimation_number && estimation.estimation_number && !estimation.estimation_number.toLowerCase().includes(filters.estimation_number.toLowerCase())) return false;
    if (filters.project_name && estimation.project_name && !estimation.project_name.toLowerCase().includes(filters.project_name.toLowerCase())) return false;
    if (filters.submission_status && estimation.submission_status !== filters.submission_status) return false;
    if (filters.award_status && (estimation.award_status !== filters.award_status || !showsAwardStatus(estimation))) return false;
    if (filters.due_date && estimation.due_date !== filters.due_date) return false;
    
    // Sales Team Member: show only estimations where they are the assigned sales person
    if (isSalesTeamMember() && estimation.sales !== user.id) {
      return false;
    }
    
    // Sales Team Lead: show self or team data based on toggle
    if (isSalesTeamLead()) {
      if (showTeamData) {
        // Team mode: show estimations assigned to any team member
        if (!teamMemberIds.includes(estimation.sales)) {
          return false;
        }
      } else {
        // Self mode: show only estimations assigned to team lead
        if (estimation.sales !== user.id) {
          return false;
        }
      }
    }
    
    return true;
  });
  const paged = usePagedList(filteredEstimations);

  const getLatestEstimationNumber = () => {
    if (estimations.length === 0) return null;
    // Sort by estimation_number to get the highest sequential number
    const sorted = [...estimations].sort((a, b) => {
      // Extract the numeric part from estimation numbers (e.g., "25ES-005" -> 5)
      const getNumber = (estNum) => {
        const match = estNum.match(/-(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      };
      return getNumber(b.estimation_number) - getNumber(a.estimation_number);
    });
    return sorted[0]?.estimation_number;
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="estimations-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small text-md3-on-surface mb-2">Estimations</h1>
            <p className="text-md3-on-surface-variant">Manage project bids with version control & multi-currency</p>
          </div>
          <div className="flex gap-4 items-center">
            {/* Toggle for Team Leads (show if user has team members) */}
            {teamDataLoaded && teamMemberIds.length > 1 && (
              <div className="flex items-center gap-2 border rounded-md3-lg px-3 py-1">
                <span className={!showTeamData ? "font-semibold text-md3-body-small" : "text-md3-body-small text-md3-on-surface-variant"}>Self</span>
                <Switch checked={showTeamData} onCheckedChange={setShowTeamData} />
                <span className={showTeamData ? "font-semibold text-md3-body-small" : "text-md3-body-small text-md3-on-surface-variant"}>Team</span>
              </div>
            )}
            {/* Download Excel - requires export permission */}
            {canExport() && (
              <>
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={handleDownloadExcel} data-testid="download-estimations-button">
                  <Download className="mr-2 h-4 w-4" /> Download Excel
                </Button>
              </>
            )}
            <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
              {/* New Estimation button - requires create permission */}
              {canCreate() && (
                <DialogTrigger asChild>
                  <Button data-testid="create-estimation-button" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                    <Plus className="mr-2 h-4 w-4" /> New Estimation
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader>
                  <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">
                    {editMode ? `Edit Estimation - ${currentEstimation?.estimation_number} v${currentEstimation?.version}` : 'Create New Estimation'}
                    {convertingFromRfq && !editMode && (
                      <Badge className="ml-2 bg-indigo-600 text-white text-xs" data-testid="converting-from-rfq-badge">
                        Converting from RFQ {convertingFromRfq.rfq_number}
                      </Badge>
                    )}
                  </DialogTitle>
                  <DialogDescription className="text-md3-body-medium text-gray-600">
                    {editMode ? 'Choose to update current version or create a new version' : 'Add a new project estimation/bid'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
                  <div className="space-y-2">
                    {!editMode && estimations.length > 0 && (
                      <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-xs text-blue-700">
                          <strong>Last Estimation Number:</strong> {getLatestEstimationNumber()}
                        </p>
                      </div>
                    )}
                    <Label htmlFor="estimation_number" className="text-md3-label-large text-gray-700 font-medium">Estimation Number *</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="estimation_number"
                      placeholder="e.g., 25ES-001 (Leave empty for auto-generation)"
                      value={formData.estimation_number}
                      onChange={(e) => setFormData({ ...formData, estimation_number: e.target.value })}
                      disabled={editMode}
                    />
                    {editMode && currentEstimation && (
                      <p className="text-xs text-blue-600">Current version: {currentEstimation.version}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Hide client field for Estimation Team Members */}
                    {!isEstimationTeamMember() && (
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="client_id" className="text-md3-label-large text-gray-700 font-medium">Client *</Label>
                        <div className="relative">
                          <Input className={`bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary${editMode ? " bg-md3-surface-container cursor-not-allowed" : ""}`} placeholder="Type to search client by number, name, or company..."
                            value={clientSearch}
                            onChange={(e) => {
                              if (editMode) return; // Disable changing client in edit mode
                              setClientSearch(e.target.value);
                              const dropdown = document.getElementById('client-dropdown');
                              if (dropdown) dropdown.style.display = 'block';
                            }}
                            onFocus={() => {
                              if (editMode) return; // Disable dropdown in edit mode
                              const dropdown = document.getElementById('client-dropdown');
                              if (dropdown) dropdown.style.display = 'block';
                            }}
                            disabled={editMode}
                          />
                          {/* Dropdown list */}
                          <div
                            id="client-dropdown"
                            className="absolute z-50 w-full mt-1 bg-md3-surface-container border border-md3-outline rounded-md3-md shadow-lg max-h-60 overflow-auto hidden"
                            style={{ display: 'none' }}
                          >
                            {getFilteredClients().length > 0 ? (
                              getFilteredClients().map(client => (
                                <div
                                  key={client.id}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                                  onClick={async () => {
                                    const selectedClient = client;
                                    const clientSoftware = selectedClient?.software?.[0] || "";
                                    
                                    // Fetch current sales user for this client
                                    let salesUserId = formData.sales;
                                    try {
                                      const response = await api.get(`/clients/${client.id}/current-sales-user`);
                                      if (response.data && response.data.sales_user_id) {
                                        salesUserId = response.data.sales_user_id;
                                      }
                                    } catch (error) {
                                      console.log("No sales user assigned to this client");
                                    }
                                    
                                    setFormData({ 
                                      ...formData, 
                                      client_id: client.id,
                                      currency: selectedClient?.currency || formData.currency,
                                      software: clientSoftware,
                                      sales: salesUserId
                                    });
                                    setClientSearch(getClientDisplay(client));
                                    document.getElementById('client-dropdown').style.display = 'none';
                                  }}
                                >
                                  <div className="font-semibold text-blue-900">{client.client_number || 'N/A'}</div>
                                  <div className="text-md3-body-small text-md3-on-surface-variant">{client.client_name || client.name || 'Unnamed Client'}</div>
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-md3-on-surface-variant text-md3-body-small">No clients found</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Client Contact for Estimation */}
                    {!isEstimationTeamMember() && formData.client_id && (
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="client_contact_id" className="text-md3-label-large text-gray-700 font-medium">Client Contact (Estimator)</Label>
                        <Select 
                          value={formData.client_contact_id || "none"} 
                          onValueChange={(value) => setFormData({ ...formData, client_contact_id: value === "none" ? "" : value })}
                        >
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue placeholder="Select client contact" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            {pickableContacts(contacts, "Estimator", formData.client_contact_id).map((contact) => (
                              <SelectItem key={contact.id} value={contact.id} disabled={contact.is_left}>
                                {contactLabel(contact)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {pickableContacts(contacts, "Estimator", formData.client_contact_id).length === 0 && (
                          <p className="text-xs text-md3-on-surface-variant">No contacts with "Estimator" role found for this client</p>
                        )}
                      </div>
                    )}

                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="project_name" className="text-md3-label-large text-gray-700 font-medium">Project Name *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="project_name"
                        placeholder="Project name"
                        value={formData.project_name}
                        onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="estimation_received_date" className="text-md3-label-large text-gray-700 font-medium">Received Date</Label>
                      <DatePicker
                        id="estimation_received_date"
                        value={formData.estimation_received_date}
                        onChange={(e) => setFormData({ ...formData, estimation_received_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="due_date" className="text-md3-label-large text-gray-700 font-medium">Due Date</Label>
                      <DatePicker
                        id="due_date"
                        value={formData.due_date}
                        min={formData.estimation_received_date || undefined}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      />
                      <div className="flex items-center gap-2 pt-1">
                        <Checkbox
                          id="high_priority"
                          checked={!!formData.high_priority}
                          onCheckedChange={(val) => setFormData({ ...formData, high_priority: !!val })}
                          data-testid="high-priority-checkbox"
                        />
                        <Label htmlFor="high_priority" className="text-md3-body-small text-red-700 font-medium cursor-pointer select-none">
                          High Priority
                        </Label>
                      </div>
                      {formData.estimation_received_date && (
                        <p className="text-xs text-md3-on-surface-variant">Must be on or after received date</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="currency" className="text-md3-label-large text-gray-700 font-medium">Currency</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 bg-md3-surface-container cursor-not-allowed" id="currency"
                        value={formData.currency === "USD" ? "US Dollar (USD)" : formData.currency === "CAD" ? "Canadian Dollar (CAD)" : formData.currency}
                        disabled
                        
                      />
                      <p className="text-xs text-md3-on-surface-variant">From client (not editable)</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="software" className="text-md3-label-large text-gray-700 font-medium">Software</Label>
                      {(() => {
                        const selectedClient = clients.find(c => c.id === formData.client_id);
                        const clientSoftware = selectedClient?.software || [];
                        
                        // If client has only one software, show as disabled input
                        if (clientSoftware.length === 1) {
                          return (
                            <>
                              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 bg-md3-surface-container cursor-not-allowed" id="software"
                                value={clientSoftware[0]}
                                disabled
                                
                              />
                              <p className="text-xs text-md3-on-surface-variant">From client (not editable)</p>
                            </>
                          );
                        }
                        
                        // If client has multiple software, show dropdown
                        if (clientSoftware.length > 1) {
                          return (
                            <>
                              <Select value={formData.software} onValueChange={(value) => setFormData({ ...formData, software: value })}>
                                <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                                  <SelectValue placeholder="Select software" />
                                </SelectTrigger>
                                <SelectContent>
                                  {clientSoftware.map((sw) => (
                                    <SelectItem key={sw} value={sw}>
                                      {sw}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-md3-on-surface-variant">Select from client's software preferences</p>
                            </>
                          );
                        }
                        
                        // If no client selected or no software
                        return (
                          <>
                            <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 bg-md3-surface-container cursor-not-allowed" id="software"
                              value=""
                              disabled
                              placeholder="No software configured"
                              
                            />
                            <p className="text-xs text-md3-on-surface-variant">Client has no software preferences</p>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <Label className="text-md3-body-medium font-semibold ">Line Items</Label>
                        <div className="flex items-center gap-3">
                          {formData.line_items.length > 1 && (
                            <div className="flex items-center gap-1 text-xs">
                              <button
                                type="button"
                                onClick={collapseAll}
                                data-testid="collapse-all-btn"
                                className="text-md3-primary hover:underline"
                              >
                                Collapse all
                              </button>
                              <span className="text-gray-300">|</span>
                              <button
                                type="button"
                                onClick={expandAll}
                                data-testid="expand-all-btn"
                                className="text-md3-primary hover:underline"
                              >
                                Expand all
                              </button>
                            </div>
                          )}
                          {formData.uses_item_wise_scope && selectedForGrouping.size >= 2 && (
                            <Button
                              type="button"
                              size="sm"
                              onClick={groupSelected}
                              data-testid="group-selected-btn"
                              className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                              Group Selected ({selectedForGrouping.size})
                            </Button>
                          )}
                          {canViewCosts() && (
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id="show_itemwise_schedule"
                                checked={formData.show_itemwise_schedule}
                                onChange={(e) => setFormData({ ...formData, show_itemwise_schedule: e.target.checked })}
                                className="h-4 w-4 rounded border-md3-outline"
                              />
                              <Label htmlFor="show_itemwise_schedule" className="text-md3-body-small font-normal cursor-pointer">
                                Item-wise schedule on Proposal
                              </Label>
                            </div>
                          )}
                        </div>
                      </div>

                      {formData.line_items.map((item, index) => {
                        const grp = getGroupForItem(item.id);
                        const grpIdx = groupIndexForItem(item.id);
                        const colorClass = grp ? groupColors[grpIdx % groupColors.length] : "";
                        const stripeClass = grp ? `border-l-4 ${groupStripeColors[grpIdx % groupStripeColors.length]}` : "";
                        const isSelected = selectedForGrouping.has(item.id);
                        const isCollapsed = collapsedItems.has(item.id);
                        // Build a compact summary used when the card is collapsed
                        const sumHours = parseFloat(item.estimated_detailing_hours) || 0;
                        const sumWeeks = parseInt(item.duration_weeks) || 0;
                        const sumDays = parseInt(item.duration_days) || 0;
                        return (
                        <div key={item.id} className={`border rounded-md3-lg p-4 pb-3 bg-md3-surface ${stripeClass}`}>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              {formData.uses_item_wise_scope && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSelectForGrouping(item.id)}
                                  data-testid={`group-select-${index}`}
                                  title="Select to group"
                                  className="h-4 w-4 rounded border-md3-outline"
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => toggleItemCollapsed(item.id)}
                                data-testid={`item-collapse-toggle-${index}`}
                                className="font-medium text-md3-body-small flex items-center gap-1 hover:text-md3-primary"
                                title={isCollapsed ? "Expand" : "Collapse"}
                              >
                                <span className="text-xs">{isCollapsed ? "▸" : "▾"}</span>
                                Item {index + 1}
                                {isCollapsed && (
                                  <span className="ml-2 text-xs text-gray-500 font-normal truncate max-w-[400px]">
                                    {item.description || "(no description)"} · {canViewCosts() ? `${formatCurrency(item.subtotal || 0, formData.currency)} · ` : ""}{sumHours}h · {sumWeeks}W {sumDays}D
                                  </span>
                                )}
                              </button>
                              {grp && (
                                <GroupBadge
                                  group={grp}
                                  index={grpIdx}
                                  colorClass={colorClass}
                                  onUngroup={() => ungroup(grp.id)}
                                  onRename={(name) => updateGroup(grp.id, { name })}
                                  onMergeMode={(merge_mode) => updateGroup(grp.id, { merge_mode })}
                                />
                              )}
                            </div>
                            {formData.line_items.length > 1 && (
                              <Button
                                type="button"
                                onClick={() => handleRemoveLineItem(item.id)}
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>

                          {!isCollapsed && (<div className="space-y-3">

                          <div className="space-y-2">
                            <Label htmlFor={`description-${item.id}`}>Description</Label>
                            <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id={`description-${item.id}`}
                              type="text"
                              placeholder="e.g., Structural and Miscellaneous"
                              value={item.description}
                              onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                            />
                          </div>

                          {canViewCosts() && (
                            <div className="grid grid-cols-3 gap-3">
                              <div className="space-y-2">
                                <Label htmlFor={`detailing-${item.id}`}>Detailing Cost</Label>
                                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id={`detailing-${item.id}`}
                                  type="text"
                                  placeholder="0.00"
                                  value={item.detailing_cost === 0 ? '' : item.detailing_cost}
                                  onChange={(e) => handleLineItemChange(item.id, 'detailing_cost', parseFloat(e.target.value) || 0)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`engineering-${item.id}`}>Engineering Cost</Label>
                                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id={`engineering-${item.id}`}
                                  type="text"
                                  placeholder="0.00"
                                  value={item.engineering_cost === 0 ? '' : item.engineering_cost}
                                  onChange={(e) => handleLineItemChange(item.id, 'engineering_cost', parseFloat(e.target.value) || 0)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Subtotal</Label>
                                <div className="text-md3-title-large text-blue-600 pt-2">
                                  {formatCurrency(item.subtotal, formData.currency)}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-2">
                              <Label htmlFor={`hours-${item.id}`}>Estimated Detailing Hours</Label>
                              <Input
                                className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary"
                                id={`hours-${item.id}`}
                                data-testid={`line-item-hours-${index}`}
                                type="number"
                                step="0.5"
                                placeholder="0"
                                value={item.estimated_detailing_hours === 0 ? '' : item.estimated_detailing_hours}
                                onChange={(e) => handleLineItemChange(item.id, 'estimated_detailing_hours', parseFloat(e.target.value) || 0)}
                              />
                              {canViewCosts() && (parseFloat(item.detailing_cost) || 0) > 0 && (parseFloat(item.estimated_detailing_hours) || 0) <= 0 && (
                                <p className="text-xs text-red-600">Required when detailing cost is set</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`weeks-${item.id}`}>Duration (Weeks)</Label>
                              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id={`weeks-${item.id}`}
                                type="text"
                                placeholder="0"
                                value={item.duration_weeks === 0 ? '' : item.duration_weeks}
                                onChange={(e) => handleLineItemChange(item.id, 'duration_weeks', parseInt(e.target.value) || 0)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`days-${item.id}`}>Duration (Days)</Label>
                              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id={`days-${item.id}`}
                                type="text"
                                placeholder="0"
                                value={item.duration_days === 0 ? '' : item.duration_days}
                                onChange={(e) => handleLineItemChange(item.id, 'duration_days', parseInt(e.target.value) || 0)}
                                data-testid={`line-item-days-${item.id}`}
                              />
                              {(parseInt(item.duration_days) || 0) > 4 && (
                                <p className="text-xs text-amber-700" data-testid={`line-item-days-warning-${item.id}`}>
                                  More than 4 days — convert to weeks (5 days = 1 week).
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Phase 3 — Per-item Scope (collapsible). Visible only in item-wise mode. */}
                          {formData.uses_item_wise_scope && (() => {
                            const inclCount = (item.scope_inclusions || "").split('\n').filter(s => s.trim()).length;
                            const exclCount = (item.scope_exclusions || "").split('\n').filter(s => s.trim()).length;
                            const isOpen = scopeOpenItems.has(item.id);
                            return (
                              <div className="border-t pt-3 mt-2">
                                <button
                                  type="button"
                                  onClick={() => toggleScopeOpen(item.id)}
                                  data-testid={`scope-toggle-${index}`}
                                  className="w-full flex items-center justify-between text-sm text-gray-700 hover:text-gray-900"
                                >
                                  <span className="font-medium">
                                    Scope {isOpen ? '▾' : '▸'}
                                    <span className="ml-2 text-xs text-gray-500">
                                      · {inclCount} incl · {exclCount} excl
                                    </span>
                                  </span>
                                </button>
                                {isOpen && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                    <div className="space-y-1">
                                      <Label className="text-xs">Inclusions</Label>
                                      <ScopeRowList
                                        value={item.scope_inclusions || ""}
                                        onChange={(v) => handleLineItemChange(item.id, 'scope_inclusions', v)}
                                        placeholder="e.g., Structural detailing"
                                        addLabel="Add Inclusion"
                                        testIdBase={`scope-incl-${index}`}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Exclusions</Label>
                                      <ScopeRowList
                                        value={item.scope_exclusions || ""}
                                        onChange={(v) => handleLineItemChange(item.id, 'scope_exclusions', v)}
                                        placeholder="e.g., Connection design"
                                        addLabel="Add Exclusion"
                                        testIdBase={`scope-excl-${index}`}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          </div>)}
                        </div>
                        );
                      })}

                      <div className="flex justify-center">
                        <Button type="button" onClick={handleAddLineItem} size="sm" variant="outline" className="w-full">
                          <Plus className="h-4 w-4 mr-1" />
                          Add Line Item
                        </Button>
                      </div>

                      <div className="border-t pt-4 space-y-2">
                        {canViewCosts() && (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-md3-body-small text-md3-on-surface-variant ">Total Detailing Cost:</Label>
                              <div className="text-md3-title-large">
                                {formatCurrency(calculateTotals(formData.line_items).totalDetailingCost, formData.currency)}
                              </div>
                            </div>
                            <div>
                              <Label className="text-md3-body-small text-md3-on-surface-variant ">Total Engineering Cost:</Label>
                              <div className="text-md3-title-large">
                                {formatCurrency(calculateTotals(formData.line_items).totalEngineeringCost, formData.currency)}
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                          {canViewCosts() && (
                            <div>
                              <Label className="text-md3-body-small text-md3-on-surface-variant ">Total Project Value:</Label>
                              <div className="text-md3-headline-large text-green-600">
                                {formatCurrency(calculateTotals(formData.line_items).totalProjectValue, formData.currency)}
                              </div>
                            </div>
                          )}
                          <div>
                            <Label className="text-md3-body-small text-md3-on-surface-variant ">Total Estimated Detailing Hours:</Label>
                            <div className="text-md3-headline-large text-blue-600" data-testid="total-estimated-hours">
                              {calculateTotals(formData.line_items).totalEstimatedHours}h
                            </div>
                          </div>
                          <div>
                            <Label className="text-md3-body-small text-md3-on-surface-variant ">Total Duration:</Label>
                            <div className="text-md3-title-large text-purple-600">
                              {calculateTotals(formData.line_items).totalWeeks}W {calculateTotals(formData.line_items).totalDays}D
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                  <div className="hidden">{/* Hidden schedule fields - now calculated from line items */}
                    <div className="space-y-2">
                      <Label htmlFor="schedule_weeks" className="text-md3-label-large text-gray-700 font-medium">Schedule (Weeks)</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="schedule_weeks"
                        type="number"
                        min="0"
                        placeholder="0"
                        value={formData.schedule_weeks}
                        onChange={(e) => setFormData({ ...formData, schedule_weeks: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="schedule_days" className="text-md3-label-large text-gray-700 font-medium">Schedule (Days)</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="schedule_days"
                        type="number"
                        min="0"
                        placeholder="0"
                        value={formData.schedule_days}
                        onChange={(e) => setFormData({ ...formData, schedule_days: e.target.value })}
                      />
                      <p className="text-xs text-md3-on-surface-variant">Max 4 days (5-day work week)</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Estimated By - Multi-select (Estimation and Management department users) */}
                    <div className="space-y-2">
                      <Label htmlFor="estimated_by" className="text-md3-label-large text-gray-700 font-medium">
                        Estimated By
                      </Label>
                      {(() => {
                        const selected = Array.isArray(formData.estimated_by) ? formData.estimated_by : (formData.estimated_by ? [formData.estimated_by] : []);
                        const options = users.filter(u => u.department === "Estimation" || u.department === "Management");
                        const labelFor = (id) => {
                          const u = options.find(o => o.id === id) || users.find(o => o.id === id);
                          return u ? `${u.first_name} ${u.last_name || ""}`.trim() : id;
                        };
                        const toggle = (id) => {
                          const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
                          setFormData({ ...formData, estimated_by: next });
                        };
                        return (
                          <Popover onOpenChange={(o) => o && setEstBySearch("")}>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                data-testid="estimated-by-trigger"
                                className="w-full justify-between bg-white border-gray-300 hover:bg-gray-50 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 font-normal"
                              >
                                <span className="truncate text-left">
                                  {selected.length === 0
                                    ? <span className="text-gray-400">Select estimator(s)</span>
                                    : selected.map(labelFor).join(", ")}
                                </span>
                                <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-2 w-[--radix-popover-trigger-width] max-h-64 overflow-y-auto bg-white"
                              align="start"
                              data-testid="estimated-by-list"
                              onWheel={(e) => { e.stopPropagation(); e.currentTarget.scrollTop += e.deltaY; }}
                            >
                              <Input
                                autoFocus
                                placeholder="Search people..."
                                value={estBySearch}
                                onChange={(e) => setEstBySearch(e.target.value)}
                                className="mb-2 h-8 text-sm sticky top-0 z-10 bg-white"
                                data-testid="estimated-by-search"
                              />
                              {options.length === 0 && <div className="px-2 py-3 text-sm text-gray-500">No eligible users</div>}
                              {options.length > 0 && !options.some(u => `${u.first_name} ${u.last_name || ""}`.toLowerCase().includes(estBySearch.trim().toLowerCase())) && (
                                <div className="px-2 py-3 text-sm text-gray-500" data-testid="estimated-by-no-match">No match</div>
                              )}
                              {options.filter(u => `${u.first_name} ${u.last_name || ""}`.toLowerCase().includes(estBySearch.trim().toLowerCase())).map(u => (
                                <label
                                  key={u.id}
                                  htmlFor={`est-by-${u.id}`}
                                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer text-sm"
                                >
                                  <Checkbox
                                    id={`est-by-${u.id}`}
                                    checked={selected.includes(u.id)}
                                    onCheckedChange={() => toggle(u.id)}
                                    data-testid={`estimated-by-opt-${u.id}`}
                                  />
                                  <span className="flex-1">{u.first_name} {u.last_name || ""} <span className="text-gray-500">({u.department})</span></span>
                                </label>
                              ))}
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                    </div>

                    {/* Priced By - Show only Management department users */}
                    <div className="space-y-2">
                      <Label htmlFor="priced_by" className="text-md3-label-large text-gray-700 font-medium">Priced By</Label>
                      <Select value={formData.priced_by} onValueChange={(value) => setFormData({ ...formData, priced_by: value })}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue placeholder="Select pricer (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {users
                            .filter(u => u.department === "Management")
                            .map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.first_name} {user.last_name || ''}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Hide sales field for Estimation Team Members */}
                    {!isEstimationTeamMember() && (
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="sales" className="text-md3-label-large text-gray-700 font-medium">Sales</Label>
                        <Select value={formData.sales} onValueChange={(value) => setFormData({ ...formData, sales: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue placeholder="Select sales person (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            {users
                              .filter(user => user.department === "Sales" || user.department === "Management")
                              .map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  {user.first_name} {user.last_name || ''} ({user.department})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Submission & Award Status Section - Highlighted */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md3-lg space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="submission_status" className="text-md3-label-large text-gray-700 font-medium">Submission Status</Label>
                        <Select value={formData.submission_status} onValueChange={(value) => setFormData((prev) => ({
                          ...prev,
                          submission_status: value,
                          status_date: ["hold", "cancelled", "rejected"].includes(value)
                            ? (["hold", "cancelled", "rejected"].includes(prev.submission_status) && prev.status_date ? prev.status_date : localTodayISO())
                            : "",
                          ...(TERMINAL_SALES_STATUS[value]
                            ? { sales_tracking_status: TERMINAL_SALES_STATUS[value], award_status: "lost" }
                            : TERMINAL_SALES_STATUS[prev.submission_status]
                              ? { sales_tracking_status: "", award_status: "pending" }
                              : {}),
                        }))}>
                          <SelectTrigger data-testid="est-form-submission-status-select" className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yet_to_start">Yet to Start</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="reviewed" data-testid="est-form-status-reviewed">Reviewed</SelectItem>
                            {canSetSubmitted && (
                              <SelectItem value="submitted" data-testid="est-form-status-submitted">Submitted</SelectItem>
                            )}
                            <SelectItem value="hold">Hold</SelectItem>
                            <SelectItem value="cancelled">Cancelled/Rejected By Client</SelectItem>
                            <SelectItem value="rejected">Internal Rejection</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(formData.submission_status === "submitted") && (
                        <div className="space-y-2">
                          <Label htmlFor="submitted_date" className="text-md3-label-large text-gray-700 font-medium">Submitted Date</Label>
                          <DatePicker
                            id="submitted_date"
                            value={formData.submitted_date}
                            onChange={(e) => setFormData({ ...formData, submitted_date: e.target.value })}
                            min={formData.estimation_received_date}
                          />
                          {formData.estimation_received_date && (
                            <p className="text-xs text-md3-on-surface-variant">
                              Must be on or after received date ({formData.estimation_received_date})
                            </p>
                          )}
                        </div>
                      )}
                      {["hold", "cancelled", "rejected"].includes(formData.submission_status) && (
                        <div className="space-y-2">
                          <Label htmlFor="status_date" className="text-md3-label-large text-gray-700 font-medium">
                            {submissionStatusLabel(formData.submission_status)} Date <span className="text-red-500">*</span>
                          </Label>
                          <DatePicker
                            id="status_date"
                            data-testid="status-date-input"
                            value={formData.status_date}
                            onChange={(e) => setFormData({ ...formData, status_date: e.target.value })}
                          />
                        </div>
                      )}
                    </div>

                    {["hold", "cancelled", "rejected"].includes(formData.submission_status) && (
                      <div className="space-y-2" data-testid="status-reason-wrap">
                        <Label htmlFor="status_reason" className="text-md3-label-large text-gray-700 font-medium">
                          Reason ({formData.submission_status === "hold" ? "On Hold" : submissionStatusLabel(formData.submission_status)}) <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          id="status_reason"
                          data-testid="status-reason-input"
                          value={formData.status_reason || ""}
                          onChange={(e) => setFormData({ ...formData, status_reason: e.target.value })}
                          placeholder={`Why is this estimation ${formData.submission_status === "hold" ? "on hold" : submissionStatusLabel(formData.submission_status).toLowerCase()}?`}
                          rows={3}
                          className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20"
                        />
                      </div>
                    )}

                    {TERMINAL_SALES_STATUS[formData.submission_status] && (
                      <div className="space-y-1" data-testid="est-form-locked-sales-status">
                        <Label className="text-md3-label-large text-gray-700 font-medium">Sales Tracking Status</Label>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-gray-100 text-gray-800 border border-gray-300 px-3 py-1.5 text-md3-body-small">{TERMINAL_SALES_STATUS[formData.submission_status]}</Badge>
                          <span className="text-xs text-md3-on-surface-variant">Locked to match the submission status</span>
                        </div>
                      </div>
                    )}

                    {(formData.submission_status === "submitted") && (
                      <div className="space-y-2">
                        <Label htmlFor="sales_tracking_status" className="text-md3-label-large text-gray-700 font-medium">Sales Tracking Status</Label>
                        <Select 
                          value={formData.sales_tracking_status || "none"} 
                          onValueChange={(value) => {
                            const selectedValue = value === "none" ? "" : value;
                            // Auto-update award_status based on sales_tracking_status
                            let awardStatus = "pending";
                            if (selectedValue === "Closed Win") {
                              awardStatus = "awarded";
                            } else if (selectedValue === "Negotiation") {
                              awardStatus = "pending";
                            } else if (selectedValue) {
                              // All other values: Closed Lost (Milekal), Lost By Client, Internal Rejection, Cancelled/Rejected By Client
                              awardStatus = "lost";
                            }
                            setFormData({ 
                              ...formData, 
                              sales_tracking_status: selectedValue,
                              award_status: awardStatus
                            });
                          }}
                        >
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            <SelectItem value="Negotiation">Negotiation</SelectItem>
                            <SelectItem value="Closed Win">Closed Win</SelectItem>
                            <SelectItem value="Closed Lost (Milekal)">Closed Lost (Milekal)</SelectItem>
                            <SelectItem value="Lost By Client">Lost By Client</SelectItem>
                            <SelectItem value="Internal Rejection">Internal Rejection</SelectItem>
                            <SelectItem value="Cancelled/Rejected By Client">Cancelled/Rejected By Client</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-md3-on-surface-variant">
                          Award Status will auto-update: Negotiation→Pending, Closed Win→Awarded, Others→Lost
                        </p>
                      </div>
                    )}

                    {(formData.submission_status === "submitted") && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="award_status" className="text-md3-label-large text-gray-700 font-medium">Award Status (Auto-updated)</Label>
                          <div className="flex items-center gap-2">
                            <Badge className={`${getAwardStatusColor(formData.award_status)} text-white px-3 py-2 text-md3-body-small`}>
                              {formData.award_status?.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-xs text-md3-on-surface-variant">
                            {formData.sales_tracking_status 
                              ? 'Auto-updated from Sales Tracking Status' 
                              : 'Set Sales Tracking Status to update this field'}
                          </p>
                        </div>
                        {formData.award_status === "awarded" && (
                          <div className="space-y-2">
                            <Label htmlFor="awarded_date" className="text-md3-label-large text-gray-700 font-medium">Awarded Date</Label>
                            <DatePicker
                              id="awarded_date"
                              value={formData.awarded_date}
                              onChange={(e) => setFormData({ ...formData, awarded_date: e.target.value })}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Proposal Scope Fields — switches between legacy (flat) and item-wise mode */}
                  {!formData.uses_item_wise_scope ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="scope_inclusions" className="text-md3-label-large text-gray-700 font-medium">Scope of Work - Inclusions</Label>
                        <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="scope_inclusions"
                          placeholder="Enter inclusions with your own formatting (one per line)&#10;• Structural & Miscellaneous steel detailing&#10;• Erection drawings&#10;• Shop drawings"
                          rows={6}
                          value={formData.scope_inclusions || ""}
                          onChange={(e) => setFormData({ ...formData, scope_inclusions: e.target.value })}
                        />
                        <p className="text-xs text-md3-on-surface-variant">Add your own formatting. Each line will appear as-is in the proposal.</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="scope_exclusions" className="text-md3-label-large text-gray-700 font-medium">Scope of Work - Exclusions</Label>
                        <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="scope_exclusions"
                          placeholder="Enter exclusions (one per line, no bullet symbols needed)"
                          rows={12}
                          value={formData.scope_exclusions || ""}
                          onChange={(e) => setFormData({ ...formData, scope_exclusions: e.target.value })}
                        />
                        <p className="text-xs text-md3-on-surface-variant">Bullet symbols (•) will be added automatically. Default exclusions are pre-filled but can be edited.</p>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="standard_exclusions" className="text-md3-label-large text-gray-700 font-medium">Standard Exclusions <span className="text-xs font-normal text-md3-on-surface-variant">(apply to entire proposal)</span></Label>
                      <Textarea
                        className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary"
                        id="standard_exclusions"
                        data-testid="standard-exclusions"
                        placeholder="Enter standard exclusions (one per line)"
                        rows={10}
                        value={formData.standard_exclusions || ""}
                        onChange={(e) => setFormData({ ...formData, standard_exclusions: e.target.value })}
                      />
                      <p className="text-xs text-md3-on-surface-variant">
                        These exclusions render at the end of the proposal's Exclusions section as plain bullets.
                        Per-item inclusions and exclusions are managed on each Line Item card above (expand the "Scope" section).
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="delivery_process" className="text-md3-label-large text-gray-700 font-medium">Delivery Process</Label>
                    <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="delivery_process"
                      placeholder="Enter delivery items (one per line, no bullet symbols needed)"
                      rows={6}
                      value={formData.delivery_process}
                      onChange={(e) => setFormData({ ...formData, delivery_process: e.target.value })}
                    />
                    <p className="text-xs text-md3-on-surface-variant">Bullet symbols (•) will be added automatically. Default values are pre-filled but can be edited.</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="remarks" className="text-md3-label-large text-gray-700 font-medium">Remarks</Label>
                    <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="remarks"
                      placeholder="Add any additional notes or comments..."
                      rows={4}
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    />
                  </div>

                  {editMode ? (
                    <div className="flex gap-2">
                      <Button type="submit" data-testid="update-current-version-btn" className={`${(currentEstimation?.submission_status === "completed" || currentEstimation?.submission_status === "submitted") ? "flex-1" : "w-full"} bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full`}>
                        Update Current Version (v{currentEstimation?.version})
                      </Button>
                      {(currentEstimation?.submission_status === "completed" || currentEstimation?.submission_status === "submitted") && (
                        <Button type="button" data-testid="create-new-version-btn" onClick={() => {
                          const changes = computeVersionChanges();
                          setVersionConfirmDialog({ open: true, changes });
                        }} className="flex-1 bg-md3-secondary hover:bg-md3-secondary/90 text-md3-on-secondary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                          Create New Version (v{(parseFloat(currentEstimation?.version || "1.0") + 0.1).toFixed(1)})
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button type="submit" className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                      Create Estimation
                    </Button>
                  )}
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Confirm New Version Dialog */}
        <Dialog open={versionConfirmDialog.open} onOpenChange={(open) => setVersionConfirmDialog({ ...versionConfirmDialog, open })}>
          <DialogContent className="max-w-2xl bg-white" data-testid="confirm-new-version-dialog">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold">
                Confirm New Version (v{(parseFloat(currentEstimation?.version || "1.0") + 0.1).toFixed(1)})
              </DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Review the changes below before creating a new version. The current version stays intact.
              </DialogDescription>
            </DialogHeader>

            {versionConfirmDialog.changes.length === 0 ? (
              <div className="rounded-md3-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="confirm-new-version-empty">
                No changes detected in Total Project Value, Total Duration or Scope of Work &ndash; Inclusions.
                You can still create a new version if you want to lock the current state as a new revision.
              </div>
            ) : (
              <div className="space-y-3" data-testid="confirm-new-version-changes">
                {versionConfirmDialog.changes.map((c, idx) => (
                  <div key={idx} className="rounded-md3-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-md3-label-large font-semibold text-md3-on-surface mb-2">{c.field}</p>
                    <div className={`grid ${c.isText ? "grid-cols-1 gap-2" : "grid-cols-2 gap-4"} text-md3-body-small`}>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-md3-on-surface-variant mb-0.5">Previous</p>
                        <p className={`${c.isText ? "whitespace-pre-wrap" : "font-mono"} text-red-700 bg-red-50 rounded px-2 py-1`}>{c.old}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-md3-on-surface-variant mb-0.5">New</p>
                        <p className={`${c.isText ? "whitespace-pre-wrap" : "font-mono"} text-green-700 bg-green-50 rounded px-2 py-1`}>{c.new}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setVersionConfirmDialog({ open: false, changes: [] })}
                className="rounded-md3-full"
                data-testid="confirm-new-version-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setVersionConfirmDialog({ open: false, changes: [] });
                  handleSubmit({ preventDefault: () => {} }, true);
                }}
                className="rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary"
                data-testid="confirm-new-version-confirm"
              >
                Confirm &amp; Create Version
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Version History</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">All versions of this estimation</DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {versionHistory.map((version, index) => {
                const bgColors = ['bg-blue-50/50 border-blue-200', 'bg-green-50/50 border-green-200', 'bg-purple-50/50 border-purple-200', 'bg-amber-50/50 border-amber-200', 'bg-pink-50/50 border-pink-200', 'bg-cyan-50/50 border-cyan-200'];
                return (
                <Card key={version.id} className={`border ${bgColors[index % bgColors.length]}`}>
                  <CardContent className="pt-6">
                    {/* Restricted view for Estimation team members */}
                    {isEstimationTeamMember() ? (
                      <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                        <div><strong>Version:</strong> {version.version}</div>
                        <div><strong>Submission Status:</strong> 
                          <Badge className={`ml-2 ${getSubmissionStatusColor(version.submission_status)}`}>
                            {submissionStatusLabel(version.submission_status)}
                          </Badge>
                        </div>
                        <div className="col-span-2"><strong>Remarks:</strong> {version.remarks || "No remarks"}</div>
                        <div><strong>Created:</strong> {new Date(version.created_at).toLocaleDateString()}</div>
                      </div>
                    ) : (
                      // Full view for other users
                      <>
                        <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                          <div><strong>Version:</strong> {version.version}</div>
                          <div><strong>Currency:</strong> {version.currency}</div>
                          {canViewCosts() && (
                            <>
                              <div><strong>Total Value:</strong> {formatCurrency(version.total_project_value, version.currency)}</div>
                              <div><strong>Detailing:</strong> {formatCurrency(version.detailing_cost, version.currency)}</div>
                              <div><strong>Engineering:</strong> {formatCurrency(version.engineering_cost, version.currency)}</div>
                            </>
                          )}
                          <div><strong>Schedule:</strong> {version.schedule_weeks}w {version.schedule_days}d</div>
                          <div><strong>Submission Status:</strong> 
                            <Badge className={`ml-2 ${getSubmissionStatusColor(version.submission_status)}`}>
                              {submissionStatusLabel(version.submission_status)}
                            </Badge>
                          </div>
                          {version.submission_status === "submitted" && (
                            <div><strong>Award Status:</strong> 
                              <Badge className={`ml-2 ${getAwardStatusColor(version.award_status)}`}>
                                {version.award_status}
                              </Badge>
                            </div>
                          )}
                          <div className="col-span-2"><strong>Remarks:</strong> {version.remarks || "No remarks"}</div>
                          <div><strong>Created:</strong> {new Date(version.created_at).toLocaleDateString()}</div>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                          <Button 
                            onClick={() => handlePreviewProposal(version.id)}
                            variant="outline"
                            size="sm"
                            className="bg-green-50 hover:bg-green-100"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            Preview Proposal
                          </Button>
                          <Button 
                            onClick={() => handleDownloadProposal(version.id)}
                            variant="outline"
                            size="sm"
                            className="bg-blue-50 hover:bg-blue-100"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Download Proposal
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
              })}
            </div>
          </DialogContent>
        </Dialog>

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-md3-title-large text-md3-on-surface ">All Estimations ({estimations.length})</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const response = await api.get("/template/estimations", { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'estimations_template.xlsx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Excel Template downloaded!");
                    } catch (error) {
                      toast.error("Failed to download template");
                    }
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Excel Template
                </Button>
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                   onClick={() => document.getElementById('bulk-upload-estimations').click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk Upload
                </Button>
                <input
                  id="bulk-upload-estimations"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    setUploadedFile(file);
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    try {
                      // Call preview endpoint
                      const response = await api.post("/import/estimations/preview", formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      
                      setImportPreviewData(response.data);
                      setImportPreviewOpen(true);
                      
                      e.target.value = '';
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to preview import");
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filter Section */}
            <div className="mb-6 p-4 bg-md3-surface rounded-md3-lg border border-md3-outline-variant">
              <h3 className="text-md3-body-small font-semibold mb-3 text-md3-on-surface-variant">Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* Hide client filter for Estimation team members */}
                {!isEstimationTeamMember() && (
                  <div className="space-y-2">
                    <Label htmlFor="filter_client" className="text-xs">Client</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_client"
                      placeholder="Search by number or name..."
                      value={filters.client_id}
                      onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="filter_estimation" className="text-xs">Estimation Number</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_estimation"
                    placeholder="Search by number..."
                    value={filters.estimation_number}
                    onChange={(e) => setFilters({ ...filters, estimation_number: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_project_name" className="text-xs">Project Name</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_project_name"
                    placeholder="Search by name..."
                    value={filters.project_name}
                    onChange={(e) => setFilters({ ...filters, project_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_due_date" className="text-xs">Due Date</Label>
                  <DatePicker
                    id="filter_due_date"
                    value={filters.due_date}
                    onChange={(e) => setFilters({ ...filters, due_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_submission" className="text-xs">Submission Status</Label>
                  <Select value={filters.submission_status || "all"} onValueChange={(value) => setFilters({ ...filters, submission_status: value === "all" ? "" : value })}>
                    <SelectTrigger id="filter_submission">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="yet_to_start">Yet to Start</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="hold">Hold</SelectItem>
                      <SelectItem value="cancelled">Cancelled/Rejected By Client</SelectItem>
                      <SelectItem value="rejected">Internal Rejection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_award" className="text-xs">Award Status</Label>
                  <Select value={filters.award_status || "all"} onValueChange={(value) => setFilters({ ...filters, award_status: value === "all" ? "" : value })}>
                    <SelectTrigger id="filter_award">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="awarded">Awarded</SelectItem>
                      <SelectItem value="partially_awarded">Partially Awarded</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-md3-on-surface-variant">
                  Showing {filteredEstimations.length} of {estimations.length} estimations
                </p>
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"  onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </div>

            {loading ? (
              <p className="text-md3-on-surface-variant">Loading...</p>
            ) : estimations.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-md3-on-surface-variant">No estimations yet. Create your first bid!</p>
              </div>
            ) : filteredEstimations.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-md3-on-surface-variant">No estimations match the selected filters.</p>
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters to see all estimations
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Estimation #</TableHead>
                      <TableHead>Version</TableHead>
                      {!isEstimationTeamMember() && <TableHead>Client</TableHead>}
                      <TableHead>Project</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Sales Person</TableHead>
                      {!isEstimationTeamMember() && <TableHead>Hours</TableHead>}
                      {canViewCosts() && !isEstimationTeamMember() && <TableHead>Total Value</TableHead>}
                      {!isEstimationTeamMember() && <TableHead>Schedule</TableHead>}
                      <TableHead>Submission</TableHead>
                      <TableHead>Award</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paged.pageItems.map((estimation) => {
                      // Row highlight for active estimations based on due date
                      const activeStatuses = ["in_progress", "yet_to_start"];
                      const todayDate = new Date();
                      const todayStr = `${todayDate.getFullYear()}-${String(todayDate.getMonth()+1).padStart(2,'0')}-${String(todayDate.getDate()).padStart(2,'0')}`;
                      const dueStr = estimation.due_date ? String(estimation.due_date).slice(0,10) : "";
                      let dueHighlight = "";
                      let dueTextClass = "text-gray-700";
                      if (activeStatuses.includes(estimation.submission_status) && dueStr) {
                        if (dueStr < todayStr) {
                          dueHighlight = "bg-red-100 hover:bg-red-200 border-l-4 border-l-red-500";
                          dueTextClass = "text-red-700 font-semibold";
                        } else if (dueStr === todayStr) {
                          dueHighlight = "bg-yellow-100 hover:bg-yellow-200 border-l-4 border-l-yellow-500";
                          dueTextClass = "text-yellow-800 font-semibold";
                        }
                      }
                      const rowClass = estimation.high_priority
                        ? "bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500"
                        : dueHighlight;
                      return (
                      <TableRow
                        key={estimation.id}
                        data-testid={estimation.high_priority ? `est-row-priority-${estimation.id}` : (dueHighlight ? `est-row-due-${estimation.id}` : undefined)}
                        className={rowClass}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {estimation.estimation_number}
                            {estimation.high_priority && (
                              <Badge className="bg-red-600 hover:bg-red-700 text-white text-[10px] px-1.5 py-0.5">
                                HIGH
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-blue-600 font-semibold">v{estimation.version}</TableCell>
                        {!isEstimationTeamMember() && <TableCell>{getClientName(estimation.client_id)}</TableCell>}
                        <TableCell>{estimation.project_name}</TableCell>
                        <TableCell className={`text-md3-body-small whitespace-nowrap ${dueTextClass}`} data-testid={`est-due-${estimation.id}`}>
                          {estimation.due_date
                            ? new Date(estimation.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                            : <span className="text-gray-400">—</span>}
                        </TableCell>
                        <TableCell data-testid={`est-sales-${estimation.id}`}>{(() => {
                          if (!estimation.sales) return "—";
                          const u = users.find(x => x.id === estimation.sales);
                          return u ? `${u.first_name} ${u.last_name || ''}`.trim() : "—";
                        })()}</TableCell>
                        {!isEstimationTeamMember() && <TableCell>{estimation.estimated_hours}h</TableCell>}
                        {canViewCosts() && !isEstimationTeamMember() && <TableCell className="font-semibold">{formatCurrency(estimation.total_project_value, estimation.currency)}</TableCell>}
                        {!isEstimationTeamMember() && <TableCell className="text-md3-body-small">{estimation.schedule_weeks}w {estimation.schedule_days}d</TableCell>}
                        <TableCell>
                          <div className="flex flex-col items-center leading-none">
                            <Badge className={getSubmissionStatusColor(estimation.submission_status)}>
                              {submissionStatusLabel(estimation.submission_status)}
                            </Badge>
                            {estimation.submitted_date && (
                              <span
                                data-testid={`est-submitted-date-${estimation.id}`}
                                className="text-[10px] font-bold text-gray-600 leading-none mt-0.5 text-center"
                              >
                                {new Date(estimation.submitted_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                              </span>
                            )}
                            {estimation.status_date && ["hold", "cancelled", "rejected"].includes(estimation.submission_status) && (
                              <span data-testid={`est-status-date-${estimation.id}`} className="text-[10px] font-bold text-gray-600 leading-none mt-0.5 text-center">
                                {new Date(estimation.status_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {showsAwardStatus(estimation) ? (
                            <div className="flex flex-col items-center leading-none">
                              <Badge className={getAwardStatusColor(estimation.award_status)}>
                                {(estimation.award_status || "pending").replace('_', ' ')}
                              </Badge>
                              {estimation.awarded_date && (
                                <span
                                  data-testid={`est-awarded-date-${estimation.id}`}
                                  className="text-[10px] font-bold text-gray-600 leading-none mt-0.5 text-center"
                                >
                                  {new Date(estimation.awarded_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                                </span>
                              )}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {/* Hide proposal view and download for Estimation team members */}
                            {!isEstimationTeamMember() && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePreviewProposal(estimation.id)}
                                  title="Preview Proposal PDF"
                                  className="border-md3-outline hover:bg-green-50 transition-all duration-200 rounded-md3-full"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadProposal(estimation.id)}
                                  title="Download Proposal PDF"
                                  className="border-md3-outline hover:bg-blue-50 transition-all duration-200 rounded-md3-full"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewHistory(estimation)}
                              title="Version History"
                              className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            {/* Manage Awards (line-item award state) — Phase 2 */}
                            {canEdit() && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setAwardsSheet({ open: true, estimationId: estimation.id })}
                                title="Manage Awards (line items)"
                                data-testid={`manage-awards-btn-${estimation.id}`}
                                className="border-md3-outline hover:bg-yellow-50 text-yellow-700 transition-all duration-200 rounded-md3-full"
                              >
                                <Trophy className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Edit button - requires edit permission */}
                            {canEdit() && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(estimation)}
                                data-testid={`est-edit-btn-${estimation.id}`}
                                title="Edit Estimation"
                                className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Delete button - requires delete permission */}
                            {canDelete() && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDelete(estimation.id)}
                                title="Delete Estimation"
                                className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Pagination
                  currentPage={paged.page}
                  totalItems={paged.total}
                  pageSize={paged.pageSize}
                  onPageChange={paged.onPageChange}
                  onPageSizeChange={paged.onPageSizeChange}
                  pageSizeOptions={[25, 50, 100]}
                  testIdPrefix="estimations-pagination"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bulk Import Preview Dialog */}
        <BulkImportPreview
          open={importPreviewOpen}
          onClose={() => setImportPreviewOpen(false)}
          previewData={importPreviewData}
          onApply={handleApplyImport}
          entityType="Estimations"
        />

        {/* Phase 2 — Manage Awards slide-out */}
        <ManageAwardsSheet
          open={awardsSheet.open}
          onOpenChange={(open) => setAwardsSheet(s => ({ ...s, open }))}
          estimationId={awardsSheet.estimationId}
          onSaved={fetchEstimations}
        />
      </div>
    </Layout>
  );
}
